import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router';
import { FullScreenLoader } from '@/components/brand';
import { useAuth } from './auth';
import { WorkspaceProvider } from './workspace';
import { ContactDrawerProvider } from './contact-drawer';
import { AudioProvider } from './audio';
import { SupportChatProvider } from './support-chat';
import { useSetupProgress } from './setup';
import { useWorkspace } from './workspace';
import { storage } from '@/lib/utils';
import { resolveDashboardEntry } from '@/lib/dashboard-entry';

import { safeNext, signupDestination } from '@/lib/auth-links';
export { safeNext } from '@/lib/auth-links';

export function RequireAuth() {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <FullScreenLoader label="Checking your session" />;
  if (status === 'signed_out' || !user) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`} replace />;
  }

  return (
    <WorkspaceProvider user={user}>
      <SupportChatProvider userId={user.id}>
        <ContactDrawerProvider>
          <AudioProvider>
            <Outlet />
          </AudioProvider>
        </ContactDrawerProvider>
      </SupportChatProvider>
    </WorkspaceProvider>
  );
}

export function GuestOnly() {
  const { status } = useAuth();
  const location = useLocation();
  const [params] = useSearchParams();
  if (status === 'loading') return <FullScreenLoader label="Checking your session" />;
  if (status === 'signed_in') {
    const destination =
      location.pathname.replace(/\/+$/, '') === '/signup'
        ? signupDestination(params.get('next'))
        : safeNext(params.get('next'));
    return <Navigate to={destination} replace />;
  }
  return <Outlet />;
}

/** Keep the dashboard unmounted until its first-entry destination is known. */
export function DashboardEntry() {
  const { orgId } = useWorkspace();
  const setup = useSetupProgress(undefined, false, false);
  const location = useLocation();
  const entry = resolveDashboardEntry({
    pathname: location.pathname,
    loading: setup.loading,
    error: setup.error,
    complete: setup.complete,
    welcomeSeen: storage.get(`cs_welcome_seen_${orgId}`, false),
  });
  if (entry === 'loading') return <FullScreenLoader label="Loading your workspace" />;
  if (entry === 'welcome') return <Navigate to={{ pathname: '/welcome', search: location.search }} replace />;
  return <Outlet />;
}
